import { useState } from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@patient-watch/ui/alert-dialog";

import {
    formBuilderQueryOptions,
    type FormBuilderData,
    useSetFormPublished,
} from "@/features/forms/api/formBuilderApi";
import { validateFormForPublish } from "@/features/forms/formPublication";
import { flushAutoSaves, useAutoSaveStatus } from "./useAutoSave";
import { useAuth } from "@/utils/auth";

export function FormPublicationControls({ form }: { form: FormBuilderData }) {
    const auth = useAuth();
    const publication = useSetFormPublished(form.form_id);
    const queryClient = useQueryClient();
    const autoSaveStatus = useAutoSaveStatus();
    const builderMutations = useIsMutating({ mutationKey: ["form-builder-save", form.form_id] });
    const [open, setOpen] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const published = form.is_published === true;

    const prepare = () => {
        void flushAutoSaves()
            .then(async () => {
                await queryClient.invalidateQueries({ queryKey: ["form-builder", form.form_id] });
                const latest = await queryClient.fetchQuery(formBuilderQueryOptions(form.form_id));
                const nextErrors = latest.is_published === true ? [] : validateFormForPublish(latest);
                setErrors(nextErrors);
                setOpen(true);
            })
            .catch(() => {
                setErrors(["Resolve failed draft saves before continuing."]);
                setOpen(true);
            });
    };

    if (form.is_licensed || !auth.roles.isAdmin) {
        return <PublicationBadge status={form.is_licensed ? "licensed" : published ? "published" : "draft"} />;
    }

    return (
        <div className="flex items-center gap-2">
            <PublicationBadge
                status={published ? "published" : "draft"}
                actionLabel={published ? "Unpublish" : "Publish"}
                onClick={prepare}
                disabled={
                    publication.isPending ||
                    autoSaveStatus === "error" ||
                    autoSaveStatus === "saving" ||
                    builderMutations > 0
                }
                title={
                    autoSaveStatus === "error"
                        ? "Resolve the failed save first"
                        : published
                          ? "Published - click to unpublish"
                          : "Draft - click to publish"
                }
            />

            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {errors.length > 0
                                ? "Cannot publish yet"
                                : published
                                  ? "Unpublish this form?"
                                  : "Publish this form?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {errors.length > 0
                                ? "Fix these items and wait for auto-save before trying again."
                                : published
                                  ? "The form will become a private draft before it can be edited."
                                  : "This makes the current saved form available for clinical use. AI will never do this automatically."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    {errors.length > 0 ? (
                        <ul className="list-disc space-y-1 pl-5 text-sm text-destructive">
                            {errors.map((error) => (
                                <li key={error}>{error}</li>
                            ))}
                        </ul>
                    ) : null}

                    <AlertDialogFooter>
                        <AlertDialogCancel>{errors.length > 0 ? "Close" : "Cancel"}</AlertDialogCancel>
                        {errors.length === 0 ? (
                            <AlertDialogAction
                                onClick={(event) => {
                                    event.preventDefault();
                                    publication.mutate(!published, { onSuccess: () => setOpen(false) });
                                }}
                            >
                                {published ? "Confirm unpublish" : "Confirm publish"}
                            </AlertDialogAction>
                        ) : null}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

const badgeTone = {
    published: { label: "Published", dot: "bg-green-500" },
    draft: { label: "Draft", dot: "bg-slate-400" },
    licensed: { label: "Licensed", dot: "bg-indigo-500" },
} as const;

/** Publication state, styled like the save status beside it. Clickable when the user can change it. */
function PublicationBadge({
    status,
    actionLabel,
    onClick,
    disabled,
    title,
}: {
    status: keyof typeof badgeTone;
    /** Accessible name for the click action, such as "Publish". */
    actionLabel?: string;
    onClick?: () => void;
    disabled?: boolean;
    title?: string;
}) {
    const tone = badgeTone[status];
    const className = "inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-muted-foreground text-xs";
    const content = (
        <>
            <span className={`rounded-full w-1.5 h-1.5 ${tone.dot}`} />
            {tone.label}
        </>
    );
    if (!onClick) return <span className={className}>{content}</span>;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            aria-label={actionLabel}
            data-testid="form-publication-badge"
            className={`${className} hover:bg-muted hover:text-foreground transition-colors disabled:opacity-60`}
        >
            {content}
        </button>
    );
}
